import styled from '@emotion/styled';
import { Region } from '@kaizenplatform/hugot-entity';
import { Meta } from '@kaizenplatform/hugot-entity/dist/entity/DynamicSchemaDefinition/IDynamicSchemaSection';
import { ButtonBase, Typography } from '@material-ui/core';
import { createElement, FC, Fragment, MouseEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { SearchAssetResult } from '../../../entity/SearchAsset';
import theme from '../../../theme';
import { isEdge } from '../../../utility/isEdge';
import SearchFileUploaderDialog from './SearchFileUploaderDialog';
import SearchFileUploaderTermsOfService from './SearchFileUploaderTermsOfService';

type Props = {
  meta: Meta;
  region: Region;
  disabled: boolean;
  isAssetTermAgreed: boolean;
  onRequestSearchAssetFileSubmit: (
    e: MouseEvent<HTMLElement>,
    selectedSearchAssetResult: SearchAssetResult[number] | null,
  ) => void;
  onAssetFormTermAgreementsConfirm: () => void;
  className?: string;
};

const SUPPORT_BROWSER_URL =
  'https://help.kaizen-ad.com/ja/articles/3788622-kaizen-ad-%E3%81%8C%E5%8B%95%E4%BD%9C%E3%81%99%E3%82%8B%E3%83%96%E3%83%A9%E3%82%A6%E3%82%B6%E4%B8%80%E8%A6%A7';

const SearchFileUploader: FC<Props> = ({
  onRequestSearchAssetFileSubmit,
  meta,
  region,
  disabled,
  isAssetTermAgreed,
  onAssetFormTermAgreementsConfirm,
  className,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [showTermsOfModal, setShowTermsOfModal] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  return (
    <>
      <FormatSelector
        disabled={disabled}
        component="div"
        onClick={() => {
          if (!isEdge()) {
            setShowDialog(true);
            setShowTermsOfModal(true);
          } else {
            window.open(SUPPORT_BROWSER_URL, '_blank');
          }
        }}
        className={className}
      >
        <UploaderIcon
          src={disabled ? '/assets/asset_form_search_form_disable.svg' : '/assets/asset_form_search_form.svg'}
        />
        <StandbyDescription variant="caption" align="center">
          <FormattedMessage id="search_file_uploader.description" />
          {isEdge() && (
            <>
              <br />
              <FormattedMessage id="search_file_uploader.description.non_supported">
                {(label: string) => <ErrorMessage>{label}</ErrorMessage>}
              </FormattedMessage>
            </>
          )}
        </StandbyDescription>
      </FormatSelector>
      <SearchFileUploaderDialog
        meta={meta}
        region={region}
        isOpen={showDialog}
        onClose={() => {
          setShowDialog(false);
        }}
        onSubmit={(e, selectedSearchAssetResult: SearchAssetResult[number] | null) => {
          setShowDialog(false);
          onRequestSearchAssetFileSubmit(e, selectedSearchAssetResult);
        }}
      />
      {!isAssetTermAgreed ? (
        <SearchFileUploaderTermsOfService
          isOpen={showTermsOfModal}
          setIsChecked={setIsChecked}
          onConfirm={() => {
            if (isChecked) {
              onAssetFormTermAgreementsConfirm();
              setIsChecked(false);
              setShowTermsOfModal(false);
            } else {
              setShowTermsOfModal(false);
            }
          }}
        />
      ) : null}
    </>
  );
};

const FormatSelector = (styled(ButtonBase)<{ disabled: boolean }>((props) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'auto auto',
  gridTemplateAreas: "'icon' 'description'",
  alignItems: 'center',
  justifyItems: 'center',
  width: '100%',
  height: '100%',
  padding: 24,
  borderRadius: 4,
  cursor: 'pointer',
  border: props.disabled ? '1px #BDBDBD solid' : '2px #F7B737 solid',
})) as unknown) as typeof ButtonBase;

const StandbyDescription = styled(Typography)`
  grid-area: description;
  height: 48px;
  ${theme.breakpoints.down('md')} {
    height: auto;
    white-space: pre-wrap;
  }
`;

const ErrorMessage = styled('span')`
  color: ${theme.palette.error.main};
`;

const UploaderIcon = styled('img')({
  width: '50%',
});

export default SearchFileUploader;
